import {Injectable} from "@angular/core";
import {Router} from '@angular/router';
import {BehaviorSubject, Observable} from "rxjs";
import {List} from 'immutable';
import {ImportSemiAutoService} from "@usercommon/service/import-semiauto.service";
import {
  RawMaterialSemiAutoLineDto,
  RawMaterialSemiautoResponseDto,
  RawMaterialService,
  SemiautoImportLineStatus
} from "@sharedcommon/rest/data.restservice";
import {LoggerService} from "@sharedcommon/services/logger.service";
import {RawmaterialOrSampleService} from "../service/rawmaterial-or-sample.service";
import {PrimengTableService} from "@sharedcommon/components/primeng-table/primeng-table.service";

/**
 * Store allowing the ochestration of semi-automatic import processing.
 */
@Injectable()
export class ImportSemiAutoStore {

  private readonly currentRm: BehaviorSubject<RawMaterialSemiAutoLineDto> = new BehaviorSubject<RawMaterialSemiAutoLineDto>(null);

  private readonly semiAutoProcessState: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private readonly totalToProcess: BehaviorSubject<number> = new BehaviorSubject(0);
  private readonly numberOfProcessedRm: BehaviorSubject<number> = new BehaviorSubject(0);
  private readonly rmsToProcess: BehaviorSubject<List<any>> = new BehaviorSubject(List([]));
  private readonly currentFilename: BehaviorSubject<string> = new BehaviorSubject('');

  private readonly lastRm: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private importMode: SemiautoImportMode = null;

  constructor(private readonly importService: ImportSemiAutoService,
              private readonly router: Router,
              private readonly rawMatService: RawMaterialService,
              private readonly logger: LoggerService,
              private readonly rawmaterialNutrientStore: RawmaterialOrSampleService) {
  }

  getCurrentRm(): Observable<RawMaterialSemiAutoLineDto> {
    return this.currentRm.asObservable();
  }

  setCurrentImportedRm(rm: RawMaterialSemiAutoLineDto): void {
    this.currentRm.next(rm);
  }

  getTotalToProcess(): Observable<number> {
    return this.totalToProcess.asObservable();
  }

  getNumerOfProcessedRm(): Observable<number> {
    return this.numberOfProcessedRm.asObservable();
  }

  getFileName(): Observable<string> {
    return this.currentFilename.asObservable();
  }

  setFileName(fileName: string): void {
    this.currentFilename.next(fileName);
  }

  /**
   * Notify the start of the semi-automatic import process.
   *
   * @param file
   * @param importMode
   */
  async startImport(file: File, importMode: SemiautoImportMode): Promise<boolean> {
    this.reset();
    this.currentFilename.next(file.name);
    // Patch the lost importMode in rawmaterial-import-semi-auto-popin (value in the are ok in popin but lost in this method ...)
    this.importMode = importMode;

    let httpResponse: RawMaterialSemiautoResponseDto = await this.importService
      .parseCsvFile(file, importMode == SemiautoImportMode.CREATION);
    if (httpResponse.rejected) {
      throw new Error(httpResponse.errorMsg);
    }

    const data: List<RawMaterialSemiAutoLineDto> = List<RawMaterialSemiAutoLineDto>(httpResponse.lines);

    if (data.size > 0) {
      const totalToProcess = data.count((dto: RawMaterialSemiAutoLineDto) => [SemiautoImportLineStatus.OK, SemiautoImportLineStatus.WARNING].includes(dto.status));

      this.totalToProcess.next(totalToProcess);

      let next: RawMaterialSemiAutoLineDto = null;
      const total = data.size;
      for (let _i = 0; _i < total; _i++) {
        const rm = data.get(_i);
        if (rm.status != SemiautoImportLineStatus.ERROR) {
          next = rm;
          if (_i == (total - 1)) {
            this.lastRm.next(true);
          }
          break;
        }
      }
      this.rmsToProcess.next(data);
      if (next != null) {
        next.processed = true;
        await this.processNextRm(next);
        this.semiAutoProcessState.next(true);
        return true;
      }
    } else {
      this.reset();
    }
    return false;
  }

  stopImport(): void {
    this.endImport();
    this.reset();
  }

  /**
   * Test is two rm are the same.
   * the rm are the same if code, and entity are identical
   *
   * @param rm1  first rm to compare
   * @param rm2  second rm to compare
   * @returns true if rm are the same
   */
  rmAreSame(rm1: RawMaterialSemiAutoLineDto, rm2: RawMaterialSemiAutoLineDto) {
    if (rm1.rawMaterial.code == rm2.rawMaterial.code) {
      if (rm1.rawMaterial.entity != null && rm2.rawMaterial.entity != null) {
        if (rm1.rawMaterial.entity.identifier == rm2.rawMaterial.entity.identifier) {
          return true;
        }
      }
    }
    return false;
  }

  /**
   * find next RM to create and process it.
   */
  async nextRmToCreate(): Promise<void> {
    // The current RM is this.currentRm,
    // const actual: RawMaterialSemiAutoLineDto = this.currentRm.value;
    const next: RawMaterialSemiAutoLineDto = this.findNextFromRm();
    if (next != null) {
      this.logger.info('logger.user.rawmaterial.import.semiauto', `Next finded rawmaterial code ${next.rawMaterial.code}`);
      await this.processNextRm(next);
    } else {
      this.logger.info('logger.user.rawmaterial.import.semiauto', 'No more rm processable. ');
      this.currentRm.next(null);
      this.numberOfProcessedRm.next(this.totalToProcess.value);
      this.lastRm.next(true);
    }
  }

  areThereStillRmsToProcess(): boolean {
    return this.rmsToProcess.value
      .filter((r: RawMaterialSemiAutoLineDto) => !r.processed && r.status != SemiautoImportLineStatus.ERROR)
      .count() > 0;
  }

  findNextFromRm(): RawMaterialSemiAutoLineDto {
    const rms = this.rmsToProcess.value;
    if (rms == null) {
      return null;
    }

    return this.rmsToProcess.value
      .filter((r: RawMaterialSemiAutoLineDto) => !r.processed && r.status != SemiautoImportLineStatus.ERROR)
      .first();
  }

  /**
   * If import is un creation mode
   * If the RM status are Ok process to create RM and goto values page
   * Otherwise (warning state) goto create page
   *
   * If the import is in modification mode load de values page.
   *
   * @param rmDto Next RM to create
   */
  async processNextRm(rmDto: RawMaterialSemiAutoLineDto) {
    rmDto.processed = true;
    this.currentRm.next(rmDto);
    this.rawmaterialNutrientStore.clearValues(true);
    this.numberOfProcessedRm.next(this.numberOfProcessedRm.value + 1);
    // If there are no more rms to process after the next one, it is the last one.
    this.lastRm.next(!this.areThereStillRmsToProcess());

    if (rmDto.status == SemiautoImportLineStatus.OK) {
      await this.processNextOkRm(rmDto);
    } else if (rmDto.status == SemiautoImportLineStatus.WARNING) {
      await this.processNextWarningRm(rmDto);
    } else {
      this.logger.debug('logger.user.rawmaterial.import.semiauto', `rawmaterial with code ${rmDto.rawMaterial.code} does note processable beause this error :${rmDto.errorMsg}`);
    }
  }

  /**
   * Process RM with state warning
   * @param rmDto
   * @private
   */
  private async processNextWarningRm(rmDto: RawMaterialSemiAutoLineDto) {
    if (this.importMode == SemiautoImportMode.CREATION) {
      // Goto create RM
      await this.router.navigate(['/app/rawmaterials/create']);
    } else if (this.importMode == SemiautoImportMode.MODIFICATION) {
      // If rm id is define it's a modification
      if (rmDto.rawMaterial.id !== null) {
        await this.router.navigate(['/app/rawmaterials/edit/' + rmDto.rawMaterial.id]);
      } else {
        // Modification but rm id does nt exist => create
        await this.router.navigate(['/app/rawmaterials/create']);
      }
    } else {
      this.logger.debug('logger.user.rawmaterial.import.semiauto', 'The import type is not defined. This shouldn\'t happen.');
    }
  }

  /**
   * Process RM with state OK
   * @param rmDto
   * @private
   */
  private async processNextOkRm(rmDto: RawMaterialSemiAutoLineDto) {
    if (this.importMode == SemiautoImportMode.CREATION) {
      this.rawMatService.create(rmDto.rawMaterial.entity.identifier, rmDto.rawMaterial)
        .subscribe(data => {
          this.logger.info('logger.user.rawmaterial.import.semiauto', `rawmaterial with code ${rmDto.rawMaterial.code} successfuly created`);

          rmDto.rawMaterial.id = data.id;
          this.router.navigate([`/app/rawmaterials/input/${data.id}`]).catch(() => {
          });
        });
    } else {
      // Semiauto modification mode
      this.logger.info('logger.user.rawmaterial.modification', `Start modification of rawmaterial with code ${rmDto.rawMaterial.code}`);
      // ALPVL1B-631 : if label or ref are modify we need to save it
      if (rmDto.labelOrReferenceModified) {
        this.rawMatService
          .put(rmDto.rawMaterial.entity.identifier, rmDto.rawMaterial.id, rmDto.rawMaterial)
          .subscribe(() => {
            this.logger.info('logger.user.rawmaterial.modification',
              `Label or reference of rawmaterial with code ${rmDto.rawMaterial.code} are saved successfully`);
            this.router.navigate([`/app/rawmaterials/input/${rmDto.rawMaterial.id}`]);
          });
      } else {
        await this.router.navigate([`/app/rawmaterials/input/${rmDto.rawMaterial.id}`]);
      }
    }
  }

  /**
   * Notify the end of the semi-automatic import process.
   */
  endImport() {
    this.semiAutoProcessState.next(false);
  }

  getSemiAutoProcessState(): Observable<boolean> {
    return this.semiAutoProcessState.asObservable();
  }

  getLastRm(): Observable<boolean> {
    return this.lastRm.asObservable();
  }

  isLastRm(): boolean {
    return this.lastRm.value;
  }

  isProcessIsOnGoing(): boolean {
    return this.semiAutoProcessState.value;
  }

  getRmsToProcess(): Observable<List<RawMaterialSemiAutoLineDto>> {
    return this.rmsToProcess.asObservable();
  }

  getRmsToProcessValue(): List<RawMaterialSemiAutoLineDto> {
    return this.rmsToProcess.value;
  }

  getCurrentRmValue(): RawMaterialSemiAutoLineDto {
    return this.currentRm.value;
  }

  reset(): void {
    this.currentRm.next(null);
    this.rmsToProcess.next(null);
    this.totalToProcess.next(0);
    this.numberOfProcessedRm.next(0);
    this.semiAutoProcessState.next(false);
    this.currentFilename.next(null);
    this.lastRm.next(false);
    this.importMode = null;
    /* Reset the semi auto import table state and do not store the former filter and sort configuration */
    PrimengTableService.resetSemiAutoImportTableState();
  }

  public getImportMode(): SemiautoImportMode {
    return this.importMode;
  }

  public setImportMode(mode: SemiautoImportMode) {
    this.importMode = mode;
  }
}

export enum SemiautoImportMode {
  CREATION = 'creation',
  MODIFICATION = 'modification'
}
